import { IconButton, makeStyles, Slide, Tooltip, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { animated as a, useSpring } from 'react-spring';
import { useTranslation } from 'src/app/i18n';
import FacebookIcon from 'src/assets/icons/common/FacebookIcon';
import LeftCircle from 'src/assets/icons/common/LeftCircle';
import LinkedInIcon from 'src/assets/icons/common/LinkedInIcon';
import TwetterIcon from 'src/assets/icons/common/TwetterIcon';
import Footer from 'src/components/layouts/Footer';
import { useResizeEnd } from 'src/hooks/useResizeEnd';
import { DESKTOP_MAX_WIDTH } from 'src/utils/helpers/theme';
import { useBlogState, useBlogAction } from './BlogProvider';
import { unSelectedBlog, toggleDetail, SelectedBlog } from './services/blog.reducer';
import { removeHtmlTags } from 'src/utils/helpers/common';
import MediumIcon from 'src/assets/icons/common/MediumIcon';
interface Props {}

const useBlogDetailStyles = makeStyles((theme) => ({
    container: {
        position: 'fixed',

        top: theme.mixins.toolbar.minHeight,
        left: 0,
        right: 0,
        // backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 20,
        overflow: 'auto',
        width: '100vw',
        height: '100vh'
    },
    blogList: {
        display: 'flex',
        minHeight: '100vh',
        width: '80%',
        maxWidth: DESKTOP_MAX_WIDTH,
        alignItems: 'flex-start',
        marginTop: theme.spacing(4),
        margin: 'auto'
    }
}));

const BlogDetail = ({}: Props) => {
    const imageContainerRef = useRef<HTMLDivElement | null>();
    const selectedEl = useRef<HTMLDivElement | null>();
    const imageBound = useRef({ left: 0, top: 0 });
    const { selectedBlog } = useBlogState();
    const classes = useBlogDetailStyles();
    useEffect(() => {
        if (selectedBlog) {
            selectedEl.current = document.querySelector(
                `[data-blog-id="${selectedBlog?.id}"]`
            ) as HTMLDivElement;
            document.body.style.overflow = 'hidden';
            selectedEl.current.style.visibility = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [selectedBlog]);

    const initCalculation = useCallback(() => {
        let lastValue = { top: 0, left: 0 };
        const interval = setInterval(() => {
            if (imageContainerRef.current) {
                imageBound.current = imageContainerRef.current.getBoundingClientRect();

                if (
                    lastValue.top === imageBound.current.top &&
                    lastValue.left === imageBound.current.left
                ) {
                    clearInterval(interval);
                }
                lastValue = imageBound.current;
            }
        }, 100);
    }, []);

    const onResizeEnd = useResizeEnd(250, initCalculation);

    useEffect(() => {
        initCalculation();
        window.addEventListener('resize', onResizeEnd);
        return () => {
            window.removeEventListener('resize', onResizeEnd);
        };
    }, [onResizeEnd, initCalculation]);

    return (
        <div
            className={classes.container}
            style={{
                pointerEvents: selectedBlog ? 'all' : 'none'
            }}>
            <div className={classes.blogList}>
                <div
                    ref={(refs) => (imageContainerRef.current = refs)}
                    style={{ pointerEvents: selectedBlog ? 'all' : 'none' }}>
                    {selectedBlog && (
                        <DetailImage
                            selectedEl={selectedEl}
                            selectedBlog={selectedBlog}
                            imageBound={imageBound.current}
                        />
                    )}
                </div>
            </div>
            {selectedBlog && <Footer variant="black" />}
        </div>
    );
};

type SelectedBlogRect = DOMRect;
const useImageStyles = makeStyles((theme) => ({
    image: {
        width: (props: SelectedBlogRect) => props?.width
    },

    imageContainer: {
        aspectRatio: '1/1',
        overflow: 'hidden',
        width: (props: SelectedBlogRect) => props?.width,
        height: (props: SelectedBlogRect) => props?.height,
        transformOrigin: 'top left'
    },
    detailText: {
        width: (props: SelectedBlogRect) => props?.width,
        marginLeft: (props: SelectedBlogRect) => props?.width / 2,
        [theme.breakpoints.down('sm')]: {
            marginTop: (props: SelectedBlogRect) => props?.width / 2
        }
    },
    detailContainer: {
        display: 'flex',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column'
        }
    },
    backIcon: { fill: theme.palette.primary.light, width: 48, height: 48 },
    backIconButton: {
        zIndex: 10,
        position: 'absolute',
        transform: `translate(-80px)`,
        alignSelf: 'flex-start'
    },
    bold: {
        fontWeight: 600
    },
    icon: { fill: theme.palette.primary.main }
}));

interface ImageProps {
    selectedBlog: NonNullable<SelectedBlog>;
    imageBound: any;
    selectedEl: any;
}

const DetailImage = ({
    selectedBlog: { bound, title, src, content, author, link },
    imageBound,
    selectedEl
}: ImageProps) => {
    const classes = useImageStyles(bound);
    const dispatch = useBlogAction();
    const [textIn, setTextIn] = useState(false);
    const { t } = useTranslation('blog');
    const transform = `translate(${bound.left - imageBound?.left}px,${
        bound.top - imageBound?.top
    }px) scale(1)`;
    const [imageContainer, set] = useSpring(() => ({
        transform
    }));
    useEffect(() => {
        setTextIn(true);
        set({ transform: 'translate(0px,0px) scale(1.3)' });
    }, []);

    return (
        <div className={classes.detailContainer}>
            <IconButton
                className={classes.backIconButton}
                onClick={() => {
                    setTextIn(false);
                    dispatch(toggleDetail(false));
                    set({
                        transform,
                        onRest: () => {
                            document.body.style.overflow = 'auto';
                            selectedEl.current.style.visibility = 'visible';
                            dispatch(unSelectedBlog());
                        }
                    });
                }}>
                <LeftCircle className={classes.backIcon} />
            </IconButton>
            <a.div className={classes.imageContainer} style={{ ...imageContainer }}>
                <img src={src} className={classes.image} />
            </a.div>
            <Slide in={textIn} direction="left">
                <div className={classes.detailText}>
                    <Typography color="textPrimary" variant="h6" className={classes.bold}>
                        {title}
                    </Typography>
                    <br />
                    <Typography color="textPrimary" variant="body2">
                        {removeHtmlTags(content)}
                    </Typography>
                    <br />
                    <Typography color="textPrimary" variant="h6" className={classes.bold}>
                        {author}
                    </Typography>
                    <Tooltip title={t('onMedium')}>
                        <a href={link}>
                            <IconButton>
                                <MediumIcon className={classes.icon} />
                            </IconButton>
                        </a>
                    </Tooltip>
                    <br />
                    <br />
                    <br />
                    <Typography color="textPrimary" variant="h6">
                        {t('share')}
                    </Typography>

                    <br />
                    <br />

                    <IconButton>
                        <LinkedInIcon className={classes.icon} />
                    </IconButton>
                    <IconButton>
                        <TwetterIcon className={classes.icon} />
                    </IconButton>
                    <IconButton>
                        <FacebookIcon className={classes.icon} />
                    </IconButton>
                </div>
            </Slide>
        </div>
    );
};

export default BlogDetail;
